import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  signal,
  untracked,
} from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { form, FormField, FormRoot, required, validateAsync } from '@angular/forms/signals';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';
import {
  WarningDialogComponent,
  WarningDialogData,
} from '../../../../components/warning-dialog/warning-dialog.component';
import { DotsLoaderComponent } from '../../../../shared/components/dots-loader/dots-loader.component';
import { TextOverflowEllipsisDirective } from '../../../../shared/directives/text-overflow-ellipsis.directive';
import { UserDetails } from '../../../auth0/models';
import { AuthApiService } from '../../../auth0/services/auth-api.service';
import { Auth0Store } from '../../../auth0/store';

const ORG_NAME_CHECK_DEBOUNCE_MS = 500;
const ORG_NAME_TAKEN = 'uniqueOrgViolation';

@Component({
  selector: 'gnx-organization-info',
  templateUrl: './organization-info.component.html',
  styleUrls: ['./organization-info.component.scss'],
  imports: [FormField, FormRoot, DotsLoaderComponent, MatTooltip, TextOverflowEllipsisDirective],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class OrganizationInfoComponent {
  private dialog = inject(MatDialog);
  private service = inject(AuthApiService);

  protected auth0Store = inject(Auth0Store);
  readonly data = input<UserDetails>(undefined);
  readonly isAdmin = input<boolean>(undefined);

  formSubmitted: boolean;
  protected editMode = signal(false);

  private orgName = linkedSignal(() => this.data().organization.name);
  protected orgNameField = form(this.orgName, (path) => {
    required(path);
    validateAsync(path, {
      when: ({ value }) => value() !== this.data().organization.name,
      params: ({ value }) => value(),
      debounce: ORG_NAME_CHECK_DEBOUNCE_MS,
      factory: (name) =>
        rxResource({ params: name, stream: ({ params }) => this.service.checkOrganizationAvailability(params) }),
      onSuccess: (result) => (result.available ? null : { kind: ORG_NAME_TAKEN, message: result.error }),
      onError: () => null,
    });
  });
  protected orgNameErrorShown = computed(
    () => this.editMode() && this.orgNameField().dirty() && this.orgNameField().invalid(),
  );
  protected orgNameRequired = computed(() => this.orgNameError('required') !== undefined);
  protected orgNameTaken = computed(() => this.orgNameError(ORG_NAME_TAKEN));

  private prevUpdateOrgInProgress: boolean;

  private updateOrgEffect = effect(() => {
    const inProgress = this.auth0Store.updateOrgInProgress();
    if (this.prevUpdateOrgInProgress && !inProgress) {
      untracked(() => this.editMode.set(false));
    }
    this.prevUpdateOrgInProgress = inProgress;
  });

  onEdit(): void {
    this.editMode.set(true);
  }

  onCancelEdit(): void {
    this.editMode.set(false);
    this.orgNameField().reset(this.data().organization.name);
  }

  onSaveWithConfirmation(): void {
    this.dialog.open(WarningDialogComponent, {
      width: '570px',
      height: '430px',
      backdropClass: 'popup-backdrop',
      panelClass: 'popup-panel',
      hasBackdrop: true,
      data: {
        title: 'Change Organization Name',
        description:
          'Are you sure you want to change the organization name? This change will be displayed for all users in your organization.',
        yesButtonLabel: 'Yes, Change Name',
        cancelButtonLabel: 'No, Go Back',
        hideTopSection: false,
        reverseButtonsOrder: false,
        yesButtonCallback: () => this.onSubmit(),
      } as WarningDialogData,
    });
  }

  onSubmit(): void {
    this.formSubmitted = true;
    this.auth0Store.updateOrgInfo({ name: this.orgName() });
  }

  private orgNameError(kind: string) {
    return this.orgNameField()
      .errors()
      .find((error) => error.kind === kind);
  }
}
